import React, { useEffect, useMemo, useState, useCallback, useRef, Suspense } from "react";
import {
  Box, Paper, Stack, Typography, TextField, Chip, Avatar,
  Accordion, AccordionSummary, AccordionDetails, Divider, List, ListItem,
  ListItemText, Skeleton, Badge, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, Collapse, IconButton, Button
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Fetch } from "../../../services/api";
import { notify } from "../../../utils/utils";
// import { BotonProcesar } from "../../../components/buttons/BotonPRocesar";
import { BotonProcesar } from "../../components/buttons/BotonProcesar";

/* ===================== Helpers de fecha ===================== */
// Hoy en UTC local (sin horas)
const today = () => new Date();
const toDDMM = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
const toISO = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const parseISO = (iso) => {
  if (!iso || !iso.includes("-")) return null;
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return Number.isFinite(dt.getTime()) ? dt : null;
};
const addMonthsSafe = (date, deltaMonths) => {
  // evita saltos de mes por días fuera de rango
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const target = new Date(year, month + deltaMonths, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(day, lastDay));
  return target;
};
const oneMonthAgoISO = () => toISO(addMonthsSafe(today(), -1));

/* ===================== Imagen de usuario por correo ===================== */
const userPhotoUrl = (nomUsuario) =>
  `https://miniowebapi.abexacloud.com/api/Archivo/verImagen?NombreCarpeta=xbest&NombreImagen=fotos-sistemas/${encodeURIComponent(
    String(nomUsuario || "").toLowerCase()
  )}`;

/* ===================== Chips de estado / estética ===================== */
const chipSx = (active, color = "#3a4457", glow = "rgba(106,163,255,.18)", height = 22) => ({
  px: 1,
  height,
  borderRadius: 2,
  fontWeight: 800,
  letterSpacing: 0.2,
  bgcolor: active ? "rgba(20,26,36,.6)" : "transparent",
  color: active ? "#eaf0ff" : "#c7d0e0",
  border: `1.3px solid ${active ? color : "#2c3442"}`,
  boxShadow: active ? `0 0 0 3px ${glow} inset` : "none",
  "& .MuiChip-label": { px: 0.75 },
});

/* ===================== Windowing simple ===================== */
const useInfiniteChunks = (list, initial = 25, step = 35) => {
  const [limit, setLimit] = useState(initial);
  const sentinelRef = useRef(null);
  useEffect(() => { setLimit(initial); }, [list, initial]);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => entry.isIntersecting && setLimit(v => Math.min(v + step, list.length)));
    }, { root: null, rootMargin: "300px 0px", threshold: 0.01 });
    io.observe(el);
    return () => io.disconnect();
  }, [list.length, step]);
  return { items: list.slice(0, limit), sentinelRef, limit };
};

/* ===================== Subcomponentes Lazy ===================== */
const TicketBlock = React.lazy(() =>
  Promise.resolve({
    default: React.memo(function TicketBlock({
      u, ticket, openTicket, onToggleTicket, matchStatus, data
    }) {
      const codUsr = u.codUsuarioResponsable;
      return (
        <Box sx={{ p: 1, border: "1px solid #2c3442", borderRadius: 3, bgcolor: "rgba(14,18,23,.6)" }}>
          {/* Encabezado clickable */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            onClick={() => onToggleTicket(codUsr, ticket.codTicket)}
            sx={{ cursor: "pointer" }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
              <FolderOpenIcon sx={{ color: "#9fb2ff" }} />
              <Typography noWrap fontWeight={900} sx={{ color: "#eaf0ff" }}>
                #{ticket.codTicket} — {ticket.nomTicket}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
              {ticket.nomEstadoTicket && (
                <Chip
                  size="small"
                  label={String(ticket.nomEstadoTicket).toUpperCase()}
                  sx={{ ...chipSx(true, "#7ea6ff", "rgba(126,166,255,.18)", 22) }}
                />
              )}
              <IconButton
                aria-label="expand ticket"
                onClick={(e) => { e.stopPropagation(); onToggleTicket(codUsr, ticket.codTicket); }}
                sx={{
                  color: "#eaf0ff", border: "1px solid #eaf0ff", borderRadius: 2, p: 0.5,
                  "&:hover": { bgcolor: "rgba(17,22,36,.6)", borderColor: "#6aa3ff" }
                }}
              >
                <ExpandMoreIcon
                  sx={{
                    transform: openTicket ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform .18s ease",
                  }}
                />
              </IconButton>
            </Stack>
          </Stack>

          <Collapse in={!!openTicket} unmountOnExit timeout="auto">
            <Divider sx={{ my: 1, borderColor: "#232a36" }} />

            {/* ENTREGABLES (Lazy anidados también) */}
            <Stack spacing={1}>
              {(ticket.entregable || [])
                .filter(ent => matchStatus(ent.nomEstadoEntregable))
                .map((ent) => {
                  const dKey = `${ticket.codTicket}:${ent.codTicketEntregable}`;
                  const openDeliv = data.openDeliverables?.has(dKey);
                  return (
                    <Suspense
                      key={ent.codTicketEntregable}
                      fallback={
                        <Box sx={{ p: 1, border: "1px solid #232a36", borderRadius: 2, bgcolor: "rgba(11,15,20,.6)" }}>
                          <Skeleton variant="text" height={20} sx={{ bgcolor: "#172033" }} />
                          <Skeleton variant="rectangular" height={36} sx={{ mt: 1, bgcolor: "#172033", borderRadius: 2 }} />
                        </Box>
                      }
                    >
                      <DeliverableBlock
                        u={u}
                        ticket={ticket}
                        ent={ent}
                        openDeliv={openDeliv}
                        onToggleDeliverable={(codU, codT, codE) => {
                          // delegado al padre (pasado por props desde arriba)
                          data._toggleDeliverable(codU, codT, codE);
                        }}
                        matchStatus={matchStatus}
                      />
                    </Suspense>
                  );
                })}
            </Stack>
          </Collapse>
        </Box>
      );
    })
  })
);

const DeliverableBlock = React.lazy(() =>
  Promise.resolve({
    default: React.memo(function DeliverableBlock({
      u, ticket, ent, openDeliv, onToggleDeliverable, matchStatus
    }) {
      const codUsr = u.codUsuarioResponsable;
      return (
        <Box sx={{ p: 1, border: "1px solid #232a36", borderRadius: 2, bgcolor: "rgba(11,15,20,.6)" }}>
          {/* Encabezado clickable */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            onClick={() => onToggleDeliverable(codUsr, ticket.codTicket, ent.codTicketEntregable)}
            sx={{ cursor: "pointer" }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Inventory2OutlinedIcon sx={{ color: "#a3bffa" }} />
              <Typography fontWeight={800} sx={{ color: "#dfe7ff" }}>
                Entregable: {ent.nomEntregable} (#{ent.codTicketEntregable})
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {ent.nomEstadoEntregable && (
                <Chip
                  size="small"
                  label={String(ent.nomEstadoEntregable).toUpperCase()}
                  sx={{ ...chipSx(true, "#9bd4b4", "rgba(155,212,180,.18)", 22) }}
                />
              )}
              <IconButton
                aria-label="expand deliverable"
                onClick={(e) => { e.stopPropagation(); onToggleDeliverable(codUsr, ticket.codTicket, ent.codTicketEntregable); }}
                sx={{
                  color: "#eaf0ff", border: "1px solid #eaf0ff", borderRadius: 2, p: 0.5,
                  "&:hover": { bgcolor: "rgba(17,22,36,.6)", borderColor: "#6aa3ff" }
                }}
              >
                <ExpandMoreIcon
                  sx={{
                    transform: openDeliv ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform .18s ease",
                  }}
                />
              </IconButton>
            </Stack>
          </Stack>

          <Collapse in={!!openDeliv} unmountOnExit timeout="auto">
            {/* ACTIVIDADES */}
            <List dense sx={{ mt: 0.5 }}>
              {(ent.actividad || [])
                .filter(act => matchStatus(act.nomEstado))
                .map((act) => (
                  <ListItem
                    key={act.codTicketActividad}
                    sx={{
                      px: 0,
                      borderBottom: "1px dashed #232a36",
                      "&:last-of-type": { borderBottom: "none" },
                    }}
                  >
                    <Stack spacing={0.5} sx={{ width: "100%" }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <AssignmentTurnedInOutlinedIcon sx={{ color: "#9bd4b4" }} />
                        <Chip
                          size="small"
                          label={String(act.nomEstado || "").toUpperCase()}
                          sx={chipSx(true, "#7ea6ff", "rgba(126,166,255,.18)", 22)}
                        />
                        <Typography fontWeight={800} sx={{ color: "#e5e9f0" }}>
                          {act.nomActividad}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <PlayCircleOutlineIcon sx={{ fontSize: 16, color: "#c7d0e0" }} />
                        <Typography variant="caption" sx={{ color: "#9aa6b8" }}>
                          {act.fechaInicioRealActividad ? `Inicio real: ${act.fechaInicioRealActividad}` : "Inicio real: —"}
                        </Typography>
                      </Stack>
                    </Stack>
                  </ListItem>
                ))}
            </List>
          </Collapse>
        </Box>
      );
    })
  })
);

/* ===================== Vista principal ===================== */
export const DistribucionActividades = () => {
  /* ===== Fechas (ISO, robustas) ===== */
  const MIN_ISO = oneMonthAgoISO(); // hoy - 1 mes
  const MAX_ISO = toISO(today());   // hoy

  const [startIso, setStartIso] = useState(MIN_ISO);
  const endIso = MAX_ISO;

  const [openDateDlg, setOpenDateDlg] = useState(false);
  const [tempStartIso, setTempStartIso] = useState(startIso); // ISO directo (evita bugs día/mes)

  /* ===== Data de usuarios (consolidado por área) ===== */
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  /* ===== Multi-selector de usuarios ===== */
  const [selectedUsers, setSelectedUsers] = useState([]); // objetos del consolidado

  /* ===== Filtros por estado (chips pequeños) ===== */
  const [showPend, setShowPend] = useState(true);
  const [showProc, setShowProc] = useState(true);
  const [showFin, setShowFin] = useState(true);

  /* ===== Detalle por usuario (al expandir) ===== */
  // Mapa por id: { loading, error, tickets: [], openTickets: Set<number>, openDeliverables: Set<string>, _toggleDeliverable: fn }
  const [userDetail, setUserDetail] = useState({});
  const [expanded, setExpanded] = useState(null);

  /* ===== UI base con fondo transparente ===== */
  const darkPaper = {
    bgcolor: "transparent",
    border: "1px solid #2c3442",
    color: "#e5e9f0",
    boxShadow: "0 6px 24px rgba(0,0,0,.35)",
    borderRadius: 5,
  };

  /* ===== Util: dd/mm para API ===== */
  const isoToDDMMYYYY = (iso) => {
    const d = parseISO(iso);
    if (!d) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  /* ===== Cargar listado consolidado ===== */
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const params = new URLSearchParams({
        FechaInicio: isoToDDMMYYYY(startIso),
        FechaFin: isoToDDMMYYYY(endIso),
        CodArea: "1",
      });
      const url = `${process.env.REACT_APP_XPROJECT_API}/Proyect/consolidatedTicketByArea?${params.toString()}`;
      const resp = await Fetch({ url });

      if (resp?.isValid) {
        setUsers(Array.isArray(resp.content) ? resp.content : []);
      } else {
        setUsers([]);
        notify(resp?.exceptions?.[0]?.description || "No se pudo cargar el consolidado", "error");
      }
    } catch {
      setUsers([]);
      notify("Error de red al cargar consolidado", "error");
    } finally {
      setLoadingUsers(false);
    }
  }, [startIso, endIso]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  /* ===== Cargar detalle de un usuario ===== */
  const fetchUserDetail = useCallback(async (usuario) => {
    if (!usuario) return;
    const codUsuario = usuario.codUsuarioResponsable ?? usuario.codUsuario ?? usuario.CodUsuario ?? null;
    if (!codUsuario) return;

    const key = String(codUsuario);
    setUserDetail((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), loading: true, error: null, openTickets: new Set(), openDeliverables: new Set() }
    }));

    try {
      const url = `${process.env.REACT_APP_XPROJECT_API}/Activity/user?` + new URLSearchParams({
        CodUsuario: String(codUsuario)
      });
      const resp = await Fetch({ url });
      const tickets = resp?.isValid && Array.isArray(resp.content) ? resp.content : [];

      // guardamos un pequeño dispatcher para toggleDeliverable que se inyecta a los bloques lazy
      const _toggleDeliverable = (codU, codT, codE) => {
        const k = String(codU);
        setUserDetail(prev => {
          const entry = prev[k];
          if (!entry) return prev;
          const openDeliverables = new Set(entry.openDeliverables);
          const id = `${codT}:${codE}`;
          openDeliverables.has(id) ? openDeliverables.delete(id) : openDeliverables.add(id);
          return { ...prev, [k]: { ...entry, openDeliverables } };
        });
      };

      setUserDetail((prev) => ({
        ...prev,
        [key]: { loading: false, error: null, tickets, openTickets: new Set(), openDeliverables: new Set(), _toggleDeliverable }
      }));
    } catch {
      setUserDetail((prev) => ({
        ...prev,
        [key]: { loading: false, error: "No se pudo cargar el detalle", tickets: [], openTickets: new Set(), openDeliverables: new Set() }
      }));
      notify("Error de red al cargar detalle", "error");
    }
  }, []);

  const handleExpand = (usuario) => async (_, isExpanded) => {
    const cod = usuario.codUsuarioResponsable ?? usuario.codUsuario ?? null;
    setExpanded(isExpanded ? cod : null);
    if (isExpanded) {
      const key = String(cod);
      const data = userDetail[key];
      if (!data || (!data.tickets && !data.loading)) {
        await fetchUserDetail(usuario);
      }
    }
  };

  /* ===== Filtro de usuarios por multiselect ===== */
  const filteredUsers = useMemo(() => {
    let base = users;
    if (selectedUsers.length) {
      const allow = new Set(selectedUsers.map(s => String(s.codUsuarioResponsable)));
      base = base.filter(u => allow.has(String(u.codUsuarioResponsable)));
    }
    return base;
  }, [users, selectedUsers]);

  /* ===== Render incremental ===== */
  const usersChunks = useInfiniteChunks(filteredUsers, 25, 35);

  /* ===== toggle de acordiones anidados ===== */
  const toggleTicket = (codUsuario, codTicket) => {
    const key = String(codUsuario);
    setUserDetail(prev => {
      const entry = prev[key];
      if (!entry) return prev;
      const openTickets = new Set(entry.openTickets);
      const id = Number(codTicket);
      openTickets.has(id) ? openTickets.delete(id) : openTickets.add(id);
      return { ...prev, [key]: { ...entry, openTickets } };
    });
  };

  /* ===== Predicado de filtro por estado ===== */
  const matchStatus = (label) => {
    const L = String(label || "").toLowerCase();
    const isPend = L.includes("pend");
    const isProc = L.includes("proce");
    const isFin  = L.includes("final");
    if (isPend && !showPend) return false;
    if (isProc && !showProc) return false;
    if (isFin  && !showFin)  return false;
    if (!isPend && !isProc && !isFin) return true;
    return true;
  };

  /* ===== Recargar ===== */
  const handleReload = async () => {
    await fetchUsers();
    setUserDetail({});
    setExpanded(null);
  };

  /* ===== Clamp seguro al aplicar ===== */
  const clampISO = (iso, minIso, maxIso) => {
    const v = parseISO(iso);
    const min = parseISO(minIso);
    const max = parseISO(maxIso);
    if (!v || !min || !max) return startIso;
    if (v < min) return minIso;
    if (v > max) return maxIso;
    return iso;
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, minHeight: "90vh", color: "#e5e9f0", bgcolor: "transparent" }}>
      {/* Barra de filtros (STICKY) */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          ...darkPaper,
          position: "sticky",
          top: 0,
          zIndex: 5,
          backdropFilter: "blur(8px)",
          background: "linear-gradient(180deg, rgba(10,14,19,0.75) 0%, rgba(10,14,19,0.35) 80%, rgba(10,14,19,0) 100%)",
          borderColor: "#334155",
        }}
      >
        <Stack direction={{ xs: "column", xl: "row" }} spacing={1.5} alignItems={{ xs: "stretch", xl: "center" }}>
          {/* Controles a la izquierda */}
          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" sx={{ flex: 1 }}>
            {/* Fecha Desde (chip) */}
            <Chip
              icon={<FilterAltOutlinedIcon sx={{ color: "#eaf0ff" }} />}
              clickable
              label={`Desde: ${toDDMM(startIso)}`}
              onClick={() => { setTempStartIso(startIso); setOpenDateDlg(true); }}
              sx={{
                px: 1.25, height: 36, borderRadius: 2, fontWeight: 800, letterSpacing: 0.2,
                bgcolor: "rgba(18,24,39,.6)", color: "#eaf0ff",
                border: "1.5px solid #eaf0ff",
                boxShadow: "0 0 0 3px rgba(106,163,255,.18) inset",
                "&:hover": { borderColor: "#6aa3ff", bgcolor: "rgba(15,23,42,.6)", cursor: "pointer" },
              }}
            />
            {/* Fecha Hasta (readonly) */}
            <Chip
              label={`Hasta: ${toDDMM(endIso)}`}
              disabled
              sx={{
                px: 1.25, height: 36, borderRadius: 2, fontWeight: 800, letterSpacing: 0.2,
                bgcolor: "rgba(15,20,28,.6)", color: "#eaf0ff",
                border: "1px dashed #eaf0ff",
              }}
            />

            {/* Multi-selector de usuarios */}
            <Autocomplete
              multiple
              options={users}
              value={selectedUsers}
              onChange={(_, value) => setSelectedUsers(value)}
              limitTags={2}
              disableCloseOnSelect
              getOptionLabel={(option) => option.nomUsuario || ""}
              isOptionEqualToValue={(o, v) => String(o.codUsuarioResponsable) === String(v.codUsuarioResponsable)}
              ListboxProps={{
                sx: {
                  bgcolor: "#0b0f14",
                  color: "#eaf0ff",
                  "& li.Mui-focused": { bgcolor: "#111624" },
                  "& li": { fontSize: 14 }
                }
              }}
              componentsProps={{
                paper: { sx: { bgcolor: "#0b0f14", color: "#eaf0ff", border: "1px solid #2c3442" } },
                popper: { sx: { zIndex: 1300 } },
                clearIndicator: { sx: { color: "#eaf0ff" } },  // “X” clara
                popupIndicator: { sx: { color: "#eaf0ff" } },  // caret claro
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Filtrar por usuarios…"
                  sx={{
                    width: 480,
                    input: { color: "#eaf0ff", caretColor: "#eaf0ff" },
                    "& .MuiChip-root": {
                      bgcolor: "rgba(21,32,54,.7)",
                      color: "#eaf0ff",
                      borderColor: "#7ea6ff",
                      "& .MuiChip-deleteIcon": { color: "#eaf0ff" }, // X clara
                    },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#eaf0ff !important" }, // borde claro
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#eaf0ff !important" }, // borde claro hover
                    "& .MuiInputBase-input::placeholder": { color: "#c7d0e0", opacity: 1 },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.codUsuarioResponsable} style={{ background: "transparent" }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar
                      src={userPhotoUrl(option.nomUsuario)}
                      alt={option.nomUsuario}
                      sx={{ width: 24, height: 24, border: "1px solid #2c3442", bgcolor: "#111624", color: "#cfd8e3", fontSize: 12 }}
                    >
                      {(option.nomUsuario || "?").slice(0, 2).toUpperCase()}
                    </Avatar>
                    <Typography sx={{ color: "#eaf0ff" }}>{option.nomUsuario}</Typography>
                    <Typography variant="caption" sx={{ color: "#9aa6b8" }}>
                      &nbsp;• Act pend: {option.actividadesPendientes || 0}
                    </Typography>
                  </Stack>
                </li>
              )}
            />

            {/* Chips de estado (pequeños) */}
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ ml: 0.5 }}>
              <Chip size="small" label="Pendiente"  clickable onClick={() => setShowPend(v=>!v)} sx={chipSx(showPend, "#a3872f", "rgba(250,204,21,.18)", 20)} />
              <Chip size="small" label="En proceso" clickable onClick={() => setShowProc(v=>!v)} sx={chipSx(showProc, "#355e9e", "rgba(106,163,255,.18)", 20)} />
              <Chip size="small" label="Finalizado" clickable onClick={() => setShowFin(v=>!v)} sx={chipSx(showFin, "#2b8f63", "rgba(103,232,168,.18)", 20)} />
            </Stack>

            {/* Botón Recargar con spinner */}
            <BotonProcesar
              onClick={handleReload}
              style={{
                border: "1px solid #eaf0ff",
                borderRadius: 8,
                padding: "6px 10px",
                color: "#eaf0ff",
                background: "transparent",
                display: "inline-flex",
                alignItems: "center",
                gap: 8
              }}
              title="Recargar datos"
            />
          </Stack>

          {/* Título a la derecha */}
          <Typography variant="h6" fontWeight={900} sx={{ ml: "auto", textAlign: "right", color: "#eaf0ff" }}>
            Distribución de actividades por usuario (Sistemas)
          </Typography>
        </Stack>
      </Paper>

      {/* Lista de usuarios (windowing) */}
      <Paper variant="outlined" sx={{ p: 1, ...darkPaper }}>
        {loadingUsers ? (
          <Box sx={{ p: 2 }}>
            {[...Array(4)].map((_, i) => (
              <Stack key={i} direction="row" alignItems="center" spacing={2} sx={{ p: 1.25 }}>
                <Skeleton variant="circular" width={42} height={42} sx={{ bgcolor: "#172033" }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" height={20} width="40%" sx={{ bgcolor: "#172033" }} />
                  <Skeleton variant="text" height={14} width="60%" sx={{ bgcolor: "#172033" }} />
                </Box>
              </Stack>
            ))}
          </Box>
        ) : usersChunks.items.length === 0 ? (
          <Typography sx={{ p: 2, color: "#9aa6b8" }}>Sin resultados para el rango/filtro.</Typography>
        ) : (
          <>
            {usersChunks.items.map((u) => {
              const key = String(u.codUsuarioResponsable);
              const data = userDetail[key];
              const hasPend = (u.actividadesPendientes || 0) > 0;

              return (
                <Accordion
                  key={key}
                  expanded={expanded === u.codUsuarioResponsable}
                  onChange={handleExpand(u)}
                  sx={{
                    bgcolor: "transparent",
                    border: "1px solid #2c3442",
                    borderRadius: 3,
                    mb: 1,
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon htmlColor="#eaf0ff" />}
                    sx={{
                      "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1.5 },
                      color: "#eaf0ff"
                    }}
                  >
                    <Badge
                      overlap="circular"
                      color={hasPend ? "warning" : "default"}
                      variant={hasPend ? "dot" : "standard"}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    >
                      <Avatar
                        src={userPhotoUrl(u.nomUsuario)}
                        alt={u.nomUsuario}
                        sx={{ width: 42, height: 42, border: "1px solid #2c3442", bgcolor: "rgba(17,22,36,.7)", color: "#eaf0ff", fontWeight: 900 }}
                      >
                        {(u.nomUsuario || "?").slice(0, 2).toUpperCase()}
                      </Avatar>
                    </Badge>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap fontWeight={900} sx={{ color: "#eaf0ff" }}>
                        {u.nomUsuario}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#9aa6b8" }}>
                        {hasPend ? "Con pendientes" : "Sin pendientes"}
                      </Typography>
                    </Box>

                    {/* KPIs rápidos */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ "& .MuiChip-root": { color: "#eaf0ff" } }}>
                      <Chip size="small" label={`Req ${u.requerimientosPendientes || 0}/${u.requerimientosFinalizados || 0}`} sx={chipSx(true, "#355e9e", "rgba(106,163,255,.18)", 20)} />
                      <Chip size="small" label={`Proy ${u.proyectosPendientes || 0}/${u.proyectosFinalizados || 0}`} sx={chipSx(true, "#7e8aa0", "rgba(160,174,192,.18)", 20)} />
                      <Chip size="small" label={`Act ${u.actividadesPendientes || 0}/${u.actividadesFinalizados || 0}`} sx={chipSx(true, "#2b8f63", "rgba(103,232,168,.18)", 20)} />
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails sx={{ bgcolor: "transparent" }}>
                    {/* Contenido del usuario */}
                    {data?.loading && (
                      <Box sx={{ p: 2 }}>
                        {[...Array(2)].map((_, i) => (
                          <Box key={i} sx={{ mb: 2, p: 1.5, border: "1px solid #232a36", borderRadius: 3, bgcolor: "rgba(14,18,23,.6)" }}>
                            <Skeleton variant="text" height={24} sx={{ bgcolor: "#172033" }} />
                            <Skeleton variant="text" height={18} width="60%" sx={{ bgcolor: "#172033" }} />
                            <Divider sx={{ my: 1.5, borderColor: "#232a36" }} />
                            <Skeleton variant="rectangular" height={54} sx={{ bgcolor: "#172033", borderRadius: 2 }} />
                          </Box>
                        ))}
                      </Box>
                    )}

                    {!data?.loading && data?.error && (
                      <Typography sx={{ color: "#ffb4b4" }}>{data.error}</Typography>
                    )}

                    {!data?.loading && data && Array.isArray(data.tickets) && data.tickets.length === 0 && (
                      <Typography sx={{ color: "#9aa6b8" }}>Sin elementos para mostrar.</Typography>
                    )}

                    {/* TICKETS (PROYECTOS) DESGLOSABLES (Lazy) */}
                    {!data?.loading && data && Array.isArray(data.tickets) && data.tickets.length > 0 && (
                      <Stack spacing={1.25}>
                        {data.tickets
                          .filter(t => matchStatus(t.nomEstadoTicket))
                          .map((ticket) => {
                            const openTicket = data.openTickets?.has(Number(ticket.codTicket));
                            return (
                              <Suspense
                                key={ticket.codTicket}
                                fallback={
                                  <Box sx={{ p: 1, border: "1px solid #2c3442", borderRadius: 3, bgcolor: "rgba(14,18,23,.6)" }}>
                                    <Skeleton variant="text" height={20} sx={{ bgcolor: "#172033" }} />
                                    <Skeleton variant="rectangular" height={40} sx={{ mt: 1, bgcolor: "#172033", borderRadius: 2 }} />
                                  </Box>
                                }
                              >
                                <TicketBlock
                                  u={u}
                                  ticket={ticket}
                                  openTicket={openTicket}
                                  onToggleTicket={(codU, codT) => {
                                    toggleTicket(codU, codT);
                                  }}
                                  matchStatus={matchStatus}
                                  data={data}
                                />
                              </Suspense>
                            );
                          })}
                      </Stack>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
            <div ref={usersChunks.sentinelRef} style={{ height: 1 }} />
          </>
        )}
      </Paper>

      {/* Diálogo de fecha Desde (ISO puro; permite cambiar año; rango máx 1 mes atrás) */}
      <Dialog
        open={openDateDlg}
        onClose={() => setOpenDateDlg(false)}
        PaperProps={{ sx: { bgcolor: "rgba(11,15,20,.9)", border: "1px solid #2c3442", color: "#eaf0ff", borderRadius: 3 } }}
      >
        <DialogTitle>Seleccionar fecha inicial</DialogTitle>
        <DialogContent dividers sx={{ borderColor: "#2c3442" }}>
          <TextField
            type="date"
            label="Desde"
            fullWidth
            InputLabelProps={{ shrink: true, sx: { color: "#eaf0ff" } }}
            value={tempStartIso}
            onChange={(e) => {
              // ISO directo -> evita bug día/mes
              setTempStartIso(e.target.value);
            }}
            inputProps={{
              min: MIN_ISO,
              max: MAX_ISO,
            }}
            sx={{
              mt: 1,
              input: { color: "#eaf0ff", caretColor: "#eaf0ff" },
              "& fieldset": { borderColor: "#eaf0ff" },
              "&:hover fieldset": { borderColor: "#eaf0ff" },
              "& input[type='date']::-webkit-calendar-picker-indicator": {
                filter: "invert(1) opacity(.9)",
                cursor: "pointer"
              }
            }}
          />
          <TextField
            label="Hasta (hoy)"
            fullWidth
            value={toDDMM(MAX_ISO)}
            disabled
            InputLabelProps={{ sx: { color: "#cfd8e3" } }}
            sx={{
              mt: 2,
              input: { color: "#cfd8e3" },
              "& fieldset": { borderColor: "#eaf0ff" },
              "& .Mui-disabled": {
                WebkitTextFillColor: "#cfd8e3 !important",
                color: "#cfd8e3 !important",
              }
            }}
          />
          <Typography variant="caption" sx={{ mt: 1.5, display: "block", color: "#9aa6b8" }}>
            * Rango máximo permitido: desde {toDDMM(MIN_ISO)} hasta {toDDMM(MAX_ISO)}.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #2c3442" }}>
          <Button onClick={() => setOpenDateDlg(false)} sx={{ color: "#eaf0ff" }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const clamped = clampISO(tempStartIso, MIN_ISO, MAX_ISO);
              setStartIso(clamped);
              setOpenDateDlg(false);
              setUserDetail({}); // reset para forzar recarga al expandir
              notify(`Rango aplicado: ${toDDMM(clamped)} — ${toDDMM(MAX_ISO)}`, "info");
            }}
            sx={{ bgcolor: "#6aa3ff", color: "#0b0f14", fontWeight: 900, "&:hover": { bgcolor: "#86b6ff" } }}
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
